import { useMemo } from 'react';
import type { DomainOption, FavoriteSite } from '../types';
import { buildFavoriteUrl } from '../utils/buildFavoriteUrl';

interface Props {
  option: DomainOption | null;
  favorites: FavoriteSite[];
  selectedFavoriteId: string;
  onFavoriteChange: (id: string) => void;
  onOpened?: (value: string) => void;
}

export function SelectedDomain({
  option,
  favorites,
  selectedFavoriteId,
  onFavoriteChange,
  onOpened,
}: Props) {
  const favorite = useMemo(
    () => favorites.find((f) => f.id === selectedFavoriteId) ?? favorites[0],
    [favorites, selectedFavoriteId]
  );

  if (!option || !favorite) {
    return (
      <div className="selected selected--empty">
        <p>Pick a number from the list, or say "number 8" to select one.</p>
      </div>
    );
  }

  const url = buildFavoriteUrl(favorite.template, option.domain);
  const isLong = option.domain.length > 60;

  const handleOpenClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Open explicitly in a new tab via window.open so we never accidentally
    // navigate the current tab (Settings link, some PWA contexts, and a few
    // mobile browsers will otherwise consume the target=_blank hint).
    // Preserves middle-click / cmd-click / right-click "Open in new tab"
    // because those bypass the click handler entirely.
    if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(option.domain).catch(() => {
        // Clipboard may be blocked (no HTTPS, no user gesture, etc.); ignore.
      });
    }
    onOpened?.(option.domain);
  };

  return (
    <div className="selected">
      <div className="selected__top">
        <div className="selected__value">
          <div className="selected__label">Selected</div>
          <div
            className={`selected__domain${isLong ? ' selected__domain--long' : ''}`}
          >
            {option.domain}
          </div>
        </div>
        <div className="selected__favorite">
          <label htmlFor="favorite-picker" className="selected__label">
            Open on
          </label>
          <select
            id="favorite-picker"
            value={selectedFavoriteId}
            onChange={(e) => onFavoriteChange(e.target.value)}
          >
            {favorites.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="selected__actions">
        <a
          className="button button--primary"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleOpenClick}
        >
          Open on {favorite.name}
        </a>
      </div>
    </div>
  );
}
